import React from "react";
import { createPortal } from "react-dom";

// 定数
const GPS_COLOR = "#0460f4";
const ACCURACY_FILL = "rgba(33,150,243,0.10)";
const ACCURACY_STROKE = "rgba(33,150,243,0.45)";

const DEFAULT_HEADING_CONE = {
  length: 80,
  width: 30,
  spreadDeg: 100,
  opacity: 0.25,
};

const degToRad = (deg) => (deg * Math.PI) / 180;
const radToDeg = (rad) => (rad * 180) / Math.PI;

const polarToCartesian = (radius, deg) => {
  const rad = degToRad(deg);
  return { x: radius * Math.cos(rad), y: radius * Math.sin(rad) };
};

const calcHalfAngleRad = (spreadDeg, width, length) => {
  if (typeof spreadDeg === "number") {
    return degToRad(spreadDeg) / 2;
  }
  const ratio = Math.max(-1, Math.min(1, width / 2 / Math.max(1e-6, length)));
  return Math.asin(ratio);
};

const buildConePath = (length, halfAngleDeg) => {
  const startDeg = -90 - halfAngleDeg;
  const endDeg = -90 + halfAngleDeg;
  const p1 = polarToCartesian(length, startDeg);
  const p2 = polarToCartesian(length, endDeg);
  const largeArc = halfAngleDeg * 2 > 180 ? 1 : 0;

  return `M 0 0 L ${p1.x} ${p1.y} A ${length} ${length} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`;
};

// 精度円
function AccuracyCircle({ radius }) {
  return (
    <circle
      id="gps-acc"
      r={radius}
      fill={ACCURACY_FILL}
      stroke={ACCURACY_STROKE}
      strokeWidth="1.5"
      vectorEffect="non-scaling-stroke"
    />
  );
}

// GPS ピン本体
function GpsPin({ radius, color }) {
  return (
    <g id="gps-pin">
      <circle cx="0" cy="0" r={radius + 3} fill="#fff">
        <animate
          attributeName="r"
          values={`${radius + 0.2};${radius + 3};${radius + 0.2}`}
          dur="1.8s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="0" cy="0" r={radius} fill={color} />
    </g>
  );
}

// 方位コーン
function HeadingCone({ headingDeg, config, fallbackColor }) {
  if (headingDeg == null || isNaN(headingDeg)) return null;

  const {
    length = DEFAULT_HEADING_CONE.length,
    width = DEFAULT_HEADING_CONE.width,
    spreadDeg = DEFAULT_HEADING_CONE.spreadDeg,
    opacity = DEFAULT_HEADING_CONE.opacity,
    fill = fallbackColor,
  } = config;

  const halfAngleRad = calcHalfAngleRad(spreadDeg, width, length);
  const halfAngleDeg = radToDeg(halfAngleRad);
  const path = buildConePath(length, halfAngleDeg);

  return (
    <g id="gps-heading" transform={`rotate(${headingDeg})`}>
      <defs>
        <radialGradient
          id="gps-marker-heading-gradient"
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          r={length}
        >
          <stop offset="0%" stopColor={fill} stopOpacity={opacity} />
          <stop offset="70%" stopColor={fill} stopOpacity={opacity * 0.5} />
          <stop offset="100%" stopColor={fill} stopOpacity="0" />
        </radialGradient>
      </defs>
      <path
        d={path}
        fill="url(#gps-marker-heading-gradient)"
        pointerEvents="none"
      />
    </g>
  );
}

// 精度円と GPS ピン、方位コーンをまとめて描画するコンポーネント
export default function GpsMarker({
  svgRoot,
  x,
  y,
  visible = true,
  zoom = 1,
  scale = 1,
  accuracy = null,
  accuracyRadius = 0,
  headingDeg = null,
  headR = 7.5,
  headingCone = DEFAULT_HEADING_CONE,
}) {
  if (!svgRoot || x == null || y == null || !visible) return null;

  const showAccuracy = accuracy != null && accuracyRadius > 0;
  const spriteScale = scale / (zoom || 1);

  return createPortal(
    <g
      id="gps-marker"
      aria-hidden="true"
      pointerEvents="none"
      transform={`translate(${x}, ${y})`}
    >
      {showAccuracy && <AccuracyCircle radius={accuracyRadius} />}

      <g id="gps-sprite" transform={`scale(${spriteScale})`}>
        <GpsPin radius={headR} color={GPS_COLOR} />
        <HeadingCone
          headingDeg={headingDeg}
          config={headingCone}
          fallbackColor={GPS_COLOR}
        />
      </g>
    </g>,
    svgRoot
  );
}
